import {useEffect} from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  nextPage,
  previousPage,
  selectPage,
  selectDataQuestions,
  questionAnswered,
  resetPage,
  selectDataComplete
} from "../features/data/dataSlice.js";
import dataIcon from "../assets/Navigation/nav_icon_quality_green.png";
import SideNav from "../components/SideNav";
import PageTitle from "../components/PageTitle";
import { Link } from "react-router-dom";
import SlideOut from "../components/SlideOut.js";
import riskStat from '../assets/Popups/popup_industry_stat_2-3.png'
import flowRates from '../assets/Popups/popup_production_graph.jpg'
import realTime from '../assets/Popups/popup_real-time_graph.jpg'
import rapidAdaptive from '../assets/Popups/popup_rapid_graph.jpg'
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { Button, Container } from "@mui/material";

const Data = () => {
  const page = useSelector(selectPage);
  const dataQuestions = useSelector(selectDataQuestions)
  const dispatch = useDispatch();
  const section = {
    title: "Data Quality & Production Insight",
    icon: dataIcon,
    pages: [1, 2, 3, 4, 5],
  };

  const isComplete = useSelector(selectDataComplete)
  useEffect(
    ()=>!isComplete ? dispatch(resetPage()):null,[]
  )

  const reduceRisk = {
    title: 'Reduce Risk',
    copy: 'Using Roxar 2600 Multiphase Flow Meter (MPFM) can help your operation reduce risk by helping avoid fines and reputation damage.',
    // bullets: ['Reduced dependency on workforce additions/reductions as operation grows/shrinks'],
    stat:{
      copy:`Reduce your measurement uncertainty to as low as 2-3% with Emerson’s Multiphase Flow Measurement Technology.*`,
      graphic: riskStat
    }
  }
  const increaseConfidence = {
    title: 'Increase Confidence',
    copy: 'Using Roxar 2600 Multiphase Flow Meter (MPFM) can provide your operation continuous, real-time data:',
    bullets: [<li>Optimize production, well performance and intervention schedule</li>,<li>Increase confidence in allocation assignments</li>],
    view:{
      vert:true,
      copy:['View chart:',<br/>,'Production/flow rates'],
      paragraph:['With 100% remote operations, no moving parts and low power consumption, compact size, the Roxar 2600 MPFM is the field proven Multiphase metering solution to be used for wellhead platforms.'],
      graphic: flowRates
    }
  }
  const troubleshootQuicker = {
    title: 'Troubleshoot Quicker',
    copy: 'Using Roxar 2600 Multiphase Flow Meter (MPFM) can provide your operation continuous, real-time data:',
    bullets: [<li> Addressing abnormal conditions quicker leads to less:</li>, <ul><li>Downtime</li><li>Production loss</li><li>Potential reservoir flow damage</li></ul>, <li>Allowing for issue identification and early troubleshooting and root cause analysis BEFORE dispatching field personnel</li>],
    view:{
      vert:true,
      copy:['View chart:',<br/>,'Real-time data'],
      paragraph:['The Multiphase meter is supported by Fieldwatch software that enable post processing of measurement data to allow for well testing without complete PVT data available at the time of testing the well. Optimizing the workflow and saving costs & time in the field for the well testing company.'],
      graphic: realTime
    }
  }
  const reduceDowntime = {
    title: 'Reduce Unplanned Downtime',
    copy: 'Using Roxar 2600 Multiphase Flow Meter (MPFM) can reduce unplanned downtime:',
    bullets: [<li> Reduce contractual and fiscal risk by ensuring product quality</li>,<li>Monitor asset health with on-board meter diagnostics</li>],
    view:{
      vert:true,
      copy:['View software:',<br/>,'Rapid Adaptive'],
      paragraph:['With 100% remote operations, no moving parts and low power consumption, compact size, the Roxar 2600 MPFM is the field proven Multiphase metering solution to be used for wellhead platforms.'],
      graphic: rapidAdaptive
    }
  }

  return (
    <Container className="content_container" maxWidth='lg'>
    <div className="roxar_content">
      <div className="main_content">
        <PageTitle section={section} currentPage={page} />
        <div className="page_contents">
          {page === 1 && (
            <div className="challenges"> 
              <div>
                <h1>Production Challenges</h1>
                <ul>
                  <li>
                  Inefficient separation leading to lost revenue from gas/oil in water streams, water in gas/<wbr/>oil streams
                  </li>
                  <li>
                  Time to troubleshoot and identify failure modes
                  </li>
                  <li>
                  Contractual/fiscal penalties due to out of tolerance product 
                  </li>
                  <li>
                  Lack of complete information to drive forward production or intervention plans  
                  </li>
                  <li>
                    Inaccurate allocation accounting
                  </li>
                </ul>
              </div>
              <div className="solution_copy">
                <p>
                Keeping product in specifications and staying in compliance with environmental regulations can be a continual challenge. Real time production data provided by Roxar 2600 Multiphase Flow Meter (MPFM) can help you stay ahead of potential issues so you can troubleshoot early.
                </p>
                <Button className="forward_button production_button" onClick={() => dispatch(nextPage())}>Learn How</Button>
              </div>
            </div>
          )}
          {page === 2 && (
            <div className="questions">
              <div>
                  <h3 className="question">Q: What percentage of your top line revenue is lost due to contractual penalties for out of spec product, or for environmental incompliances?</h3>
                  <div className="answers short_answers">
                      <Button className={`answer ${dataQuestions[0].answer=='0%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'0%', score:5, max: 5}))}> 0% </Button>
                      <Button className={`answer ${dataQuestions[0].answer=='<1%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'<1%', score:4, max: 5}))}> &lt;1%</Button>
                      <Button className={`answer ${dataQuestions[0].answer=='1-3%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'1-3%', score:3, max: 5}))}> 1-3%</Button>
                      <Button className={`answer ${dataQuestions[0].answer=='3-10%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'3-10%', score:2, max: 5}))}> 3-10%</Button>
                      <Button className={`answer ${dataQuestions[0].answer=='>10%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:0,answer:'>10%', score:1, max: 5}))}> &gt;10%</Button>
                  </div>
              </div>
              <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Next</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 3 && (
              <div className="questions">
              
                  <h3 className="question">Q: What is your average variance between individual well production allocation and overall lease/pad production?</h3>
                  <div className="answers short_answers">
                  <Button className={`answer ${dataQuestions[1].answer=='1-5%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'1-5%', score:4, max: 4}))}> 1-5%</Button>
                  <Button className={`answer ${dataQuestions[1].answer=='5-10%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'5-10%', score:3, max: 4}))}> 5-10%</Button>
                  <Button className={`answer ${dataQuestions[1].answer=='10-15%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'10-15%', score:2, max: 4}))}> 10-15%</Button>
                  <Button className={`answer ${dataQuestions[1].answer=='>15%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:1,answer:'>15%', score:1, max: 4}))}> &gt;15%</Button>   
                  </div>
              
                  <h3 className="question">Q: What is your ideal variance?</h3>
                  <div className="answers short_answers">
                  <Button className={`answer ${dataQuestions[2].answer=='1-5%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'1-5%', score:'1-5%' === dataQuestions[1].answer?4:2, max: 4}))}> 1-5%</Button>
                  <Button className={`answer ${dataQuestions[2].answer=='5-10%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'5-10%', score:'5-10%' === dataQuestions[1].answer?4:2, max: 4}))}> 5-10%</Button>
                  <Button className={`answer ${dataQuestions[2].answer=='10-15%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'10-15%', score:'10-15%' === dataQuestions[1].answer?4:2, max: 4}))}> 10-15%</Button>
                  <Button className={`answer ${dataQuestions[2].answer=='>15%'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:2,answer:'>15%', score:'>15%' === dataQuestions[1].answer?4:2, max: 4}))}> &gt;15%</Button>   
                  </div>
           
              
              <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Next</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 4 && (
              <div className="questions"> 
                  <h3 className="question">Q: How far out is your current maintenance cycle planned?</h3>
                  <div className="answers">
                  <Button className={`answer ${dataQuestions[3].answer=='1-3 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:3,answer:'1-3 weeks', score:1, max: 5}))}> 1-3 weeks</Button>
                  <Button className={`answer ${dataQuestions[3].answer=='4-6 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:3,answer:'4-6 weeks', score:2, max: 5}))}> 4-6 weeks</Button>
                  <Button className={`answer ${dataQuestions[3].answer=='7-9 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:3,answer:'7-9 weeks', score:3, max: 5}))}> 7-9 weeks</Button>
                  <Button className={`answer ${dataQuestions[3].answer=='10-12 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:3,answer:'10-12 weeks', score:4, max: 5}))}> 10-12 weeks</Button>
                  <Button className={`answer ${dataQuestions[3].answer=='>12 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:3,answer:'>12 weeks', score:5, max: 5}))}>  &gt;12 weeks</Button>   
              </div>
                  <h3 className="question">Q: How far out would you like your maintenance cycle planned?</h3>
                  <div className="answers">
                  <Button className={`answer ${dataQuestions[4].answer=='1-3 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:4,answer:'1-3 weeks', score:'1-3 weeks' === dataQuestions[3].answer?4:2, max: 4}))}> 1-3 weeks</Button>
                  <Button className={`answer ${dataQuestions[4].answer=='4-6 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:4,answer:'4-6 weeks', score:'4-6 weeks' === dataQuestions[3].answer?4:2, max: 4}))}> 4-6 weeks</Button>
                  <Button className={`answer ${dataQuestions[4].answer=='7-9 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:4,answer:'7-9 weeks', score:'7-9 weeks' === dataQuestions[3].answer?4:2, max: 4}))}> 7-9 weeks</Button>
                  <Button className={`answer ${dataQuestions[4].answer=='10-12 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:4,answer:'10-12 weeks', score:'10-12 weeks' === dataQuestions[3].answer?4:2, max: 4}))}> 10-12 weeks</Button>
                  <Button className={`answer ${dataQuestions[4].answer=='>12 weeks'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:4,answer:'>12 weeks', score:'>12 weeks' === dataQuestions[3].answer?4:2, max: 4}))}> &gt;12 weeks</Button>   
                  </div>
              
              <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button" onClick={() => dispatch(nextPage())}><span>Next</span><ArrowForwardIos /></Button>
              </div>
            </div>
          )}
          {page === 5 && (
              <div className="questions">
                  <h3 className="question">Q: Do you have trouble with reliability/repeatability of test measurements?</h3>
                  <div className="answers">
                      <Button className={`answer ${dataQuestions[5].answer=='Yes'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:5,answer:'Yes', score:2, max: 4}))}>Yes</Button>
                      <Button className={`answer ${dataQuestions[5].answer=='No'?'active_answer':null}`} onClick={(e)=> dispatch(questionAnswered({question:5,answer:'No', score:4, max: 4}))}>No</Button>
              </div>
              
              <div className="page_nav">
              <Button className="prev_button" onClick={() => dispatch(previousPage())}><ArrowBackIos /><span>Previous</span></Button>
                  <Button className="next_button"  component={Link} to={'/expertise'}><span>Next</span><ArrowForwardIos /></Button>
              </div>
              <div>
                  <Button component={Link} to='/results' className="next_button view_results">View Section Results</Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {page === 2 && (
      <SlideOut title={reduceRisk.title} copy={reduceRisk.copy}   stat={reduceRisk.stat}/>
    )}
      {page === 3 && (
      <SlideOut title={increaseConfidence.title} copy={increaseConfidence.copy}  bullets={increaseConfidence.bullets} view={increaseConfidence.view}/>
    )}
      {page === 4 && (
      <SlideOut title={troubleshootQuicker.title} copy={troubleshootQuicker.copy}   bullets={troubleshootQuicker.bullets} view={troubleshootQuicker.view}/>
    )}
      {page === 5 && (
      <SlideOut title={reduceDowntime.title} copy={reduceDowntime.copy}  bullets={reduceDowntime.bullets} view={reduceDowntime.view}/>
    )}
      <SideNav />
    </div>
    </Container>
  );
};

export default Data;
